import {AuthModel} from './_models'
import axiosInstance from 'axios'

const AUTH_LOCAL_STORAGE_KEY = 'kt-auth-react-v'
const AUTH_CHANGED_EVENT = 'altrego-auth-changed'
const API_URL = process.env.REACT_APP_API_URL
let refreshRequest: Promise<AuthModel> | null = null
let currentAuth: AuthModel | undefined

const readLegacyAuth = (): AuthModel | undefined => {
  try {
    const stored = localStorage?.getItem(AUTH_LOCAL_STORAGE_KEY)
    return stored ? JSON.parse(stored) as AuthModel : undefined
  } catch (error) {
    localStorage?.removeItem(AUTH_LOCAL_STORAGE_KEY)
    return undefined
  }
}

let legacyAuth = readLegacyAuth()

const getAuth = (): AuthModel | undefined => {
  return currentAuth
}

const setAuth = (auth: AuthModel) => {
  currentAuth = {...auth}
  delete currentAuth.api_token
  legacyAuth = undefined
  localStorage?.removeItem(AUTH_LOCAL_STORAGE_KEY)
  window.dispatchEvent(new CustomEvent(AUTH_CHANGED_EVENT, {detail: currentAuth}))
}

const removeAuth = () => {
  currentAuth = undefined
  legacyAuth = undefined
  localStorage?.removeItem(AUTH_LOCAL_STORAGE_KEY)
  window.dispatchEvent(new CustomEvent(AUTH_CHANGED_EVENT))
}

const refreshAuth = (): Promise<AuthModel> => {
  if (!refreshRequest) {
    refreshRequest = axiosInstance
      .post<AuthModel>(
        `${API_URL}/verifyToken`,
        legacyAuth?.api_token ? {api_token: legacyAuth.api_token} : {},
        {skipAuthRefresh: true, withCredentials: true} as any
      )
      .then(({data}) => {
        setAuth(data)
        return data
      })
      .catch((refreshError) => {
        if (refreshError.response?.status === 401) removeAuth()
        throw refreshError
      })
      .finally(() => {
        refreshRequest = null
      })
  }
  return refreshRequest
}

export function setupAxios(axios: any) {
  axios.defaults.headers.Accept = 'application/json'
  axios.defaults.withCredentials = true
  axios.interceptors.request.use(
    (config: any) => {
      const auth = getAuth()
      if (auth && auth.access_token && !config.skipAuthRefresh) {
        config.headers = config.headers || {}
        config.headers.Authorization = `Bearer ${auth.access_token}`
      }

      return config
    },
    (err: any) => Promise.reject(err)
  )


  axios.interceptors.response.use(
    (response: any) => response,
    async (error: any) => {
      const originalRequest = error.config
      const status = error.response?.status

      if (status !== 401 || !originalRequest || originalRequest._retry || originalRequest.skipAuthRefresh) {
        return Promise.reject(error)
      }

      originalRequest._retry = true
      const renewedAuth = await refreshAuth()
      originalRequest.headers = originalRequest.headers || {}
      originalRequest.headers.Authorization = `Bearer ${renewedAuth.access_token}`
      return axios(originalRequest)
    }
  )
}

export {getAuth, setAuth, removeAuth, refreshAuth, AUTH_LOCAL_STORAGE_KEY, AUTH_CHANGED_EVENT}
